import cn from 'classnames';
import DropdownSelect from 'components/Common/DropdownSelect/DropdownSelect';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import OpText, { TextVariant } from 'components/Common/op-ui-wrappers/OpText/OpText';
import DesktopOnly from 'components/Common/Utils/DesktopOnly';
import MobileOnly from 'components/Common/Utils/MobileOnly';
import SearchFilter from 'components/SearchPage/SearchFilter/SearchFilter';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useAppDispatch } from 'store';
import isDesktop from 'store/app/selector/isDesktop';
import updateSearchQueryStringOperation from 'store/search/operation/updateSearchQueryStringOperation';
import getCurrentResults from 'store/search/selector/getCurrentResults';
import getIsAnyFiltersDefined from 'store/search/selector/getIsAnyFiltersDefined';
import getIsShowSearchFilters from 'store/search/selector/getIsShowSearchFilters';
import getOffset from 'store/search/selector/getOffset';
import getSearchQuery from 'store/search/selector/getSearchQuery';
import getSearchSortBy from 'store/search/selector/getSearchSortBy';
import getTotalResultsAvailable from 'store/search/selector/getTotalResultsAvailable';
import isSearchLoading from 'store/search/selector/isSearchLoading';
import {
  clearSearchFiltersAction,
  setSearchPageNrAction,
  setSearchRefetchFlagAction,
  setSearchSortByAction,
  toggleShowSearchFiltersAction,
} from 'store/search/slice';
import { SearchResultProduct, SearchSortBy } from 'store/search/types';
import getStudioTotalResultsAvailable from 'store/studioSearch/selector/getStudioTotalResultsAvailable';
import gtmSendEvent, { gtmChangeSortingOnSearchPageEvent } from 'utils/gtm/gtmSendEvent';
import gtmSendSearchResultsEventOperation from 'store/category/operation/gtmSendSearchResultsEventOperation';
import fetchGTMSearchProductsDataOperation from 'store/search/operation/fetchGTMSearchProductsDataOperation';
import LoadMoreItemsBefore from 'components/SearchPage/ItemsList/LoadMoreItemsBefore/LoadMoreItemsBefore';
import Keywords from './Keywords/Keywords';
import LoadMoreItems from './LoadMoreItems/LoadMoreItems';
import ProductItem from './ProductItem/ProductItem';
import styles from './ItemsList.module.scss';

export const sortOptions = [
  {
    title: 'txt_sort_by_most_relevant',
    value: 'mostRelevant',
  },
  // {
  //   title: 'txt_sort_by_popular',
  //   value: 'mostPopular',
  // },
  {
    title: 'txt_sort_by_newest',
    value: 'created_at',
  },
  // {
  //   title: 'txt_sort_by_best_selling',
  //   value: 'bestSelling',
  // },
] as { title: string; value: SearchSortBy }[];

interface ItemsListProps {
  showSorting?: boolean;
  showQuery?: boolean;
}

const ItemsList = ({ showSorting = true, showQuery = true }: ItemsListProps) => {
  const isDesktopDevice = useSelector(isDesktop);
  const isLoading = useSelector(isSearchLoading);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const totalResults = useSelector(getTotalResultsAvailable);
  const totalImages = useSelector(getStudioTotalResultsAvailable);
  const searchQuery = useSelector(getSearchQuery);
  const results = useSelector(getCurrentResults);
  const offset = useSelector(getOffset);
  const isShowFilters = useSelector(getIsShowSearchFilters);
  const filtersDefined = useSelector(getIsAnyFiltersDefined);
  const sortBy = useSelector(getSearchSortBy);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { products } = results;

  useEffect(() => {
    if (products?.length) {
      dispatch(fetchGTMSearchProductsDataOperation());
      dispatch(gtmSendSearchResultsEventOperation());
    }
  }, [products]);

  if (!totalResults) {
    return null;
  }

  const toggleFilters = () => {
    dispatch(toggleShowSearchFiltersAction());
  };

  const applyFilters = () => {
    dispatch(setSearchRefetchFlagAction(true));
    dispatch(updateSearchQueryStringOperation(history));
    setShowMobileFilters(!showMobileFilters);
  };

  const clearFilters = () => {
    dispatch(clearSearchFiltersAction());
    dispatch(setSearchPageNrAction(1));
    dispatch(updateSearchQueryStringOperation(history));
  };

  const changeSortBy = (sort: SearchSortBy) => {
    gtmSendEvent(gtmChangeSortingOnSearchPageEvent(sort));
    dispatch(setSearchSortByAction(sort));
    dispatch(setSearchPageNrAction(1));
    dispatch(setSearchRefetchFlagAction(true));
    dispatch(updateSearchQueryStringOperation(history));
  };

  return (
    <div className={cn(styles.itemsWrapper, 'productsWrapper', { [styles.loading]: isLoading })}>
      {showQuery && (
        <>
          <DesktopOnly>
            <OpText block variant="headline-medium-regular">
              <span>{t('txt_search_results_for_short')}</span>
              <b>{` “${searchQuery}”`}</b>
            </OpText>
          </DesktopOnly>

          <OpText block variant={`body-${isDesktopDevice ? 'large' : 'medium'}-light` as TextVariant}>
            <Trans
              t={t}
              i18nKey={totalImages ? 'txt_search_results_count_with_images' : 'txt_search_results_count'}
              values={{ total: totalResults }}
              components={{ u: <u style={{ fontWeight: 400 }} /> }}
            />
          </OpText>
        </>
      )}

      <DesktopOnly and={filtersDefined}>
        <div className={styles.appliedFilters}>
          <OpButton size="small" variant="primary" icon-right="close" onclick={clearFilters}>
            {t('txt_clear_all')}
          </OpButton>

          <Keywords />
        </div>
      </DesktopOnly>

      <div className={styles.sortRow}>
        <DesktopOnly>
          <OpButton
            icon-right={isShowFilters ? 'eye-off' : 'eye'}
            size="medium"
            variant="secondary"
            onClick={toggleFilters}
          >
            {showMobileFilters ? t('txt_hide_filters') : t('txt_show_filters')}
          </OpButton>
        </DesktopOnly>

        <MobileOnly>
          <OpButton
            size="medium"
            variant="secondary"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            icon-left="filter"
          >
            <span className={styles.upperCase}>{t('txt_filters')}</span>
          </OpButton>

          <SearchFilter
            visible={showMobileFilters}
            onClose={() => setShowMobileFilters(!showMobileFilters)}
            onShow={() => applyFilters()}
          />
        </MobileOnly>

        {showSorting && (
          <DropdownSelect
            label={t('txt_sorting_by')}
            titleDecorator={t}
            options={sortOptions}
            selected={sortBy}
            onChange={changeSortBy}
          />
        )}
      </div>

      <div className={styles.loadMoreItems}>
        <LoadMoreItemsBefore />
      </div>
      <div className={styles.items}>
        {products?.map((product: SearchResultProduct, index: number) => (
          <ProductItem key={product.sku} index={1 + offset + index} product={product} forceReload />
        ))}
      </div>

      <div className={styles.loadMoreItems}>
        <LoadMoreItems />
      </div>
    </div>
  );
};

export default ItemsList;
